/**
 * NotificationProvider
 *
 * Mounted ONCE in App.tsx. Owns the global notification state and the
 * SSE lifecycle. Two singleton SSE connections per session:
 *
 *   1. NotificationSSEClient        — /api/notifications/inbox/stream
 *      pushes: notification + unread-count
 *   2. CentralEventStreamClient     — /api/events/stream
 *      pushes: workspace.member.* + entitlements/credits/subscription/feature-flags
 *
 * Both clients come from shared packages (@levelup/central-notifications,
 * @levelup/central-events). No custom SSE parsing.
 *
 * Bearer-only. No cookies. No proxy.
 *
 * The provider:
 *   - Pre-fetches initial inbox + unread count via REST
 *   - Connects both SSE clients when boot is ready + token + workspaceId exist
 *   - Disconnects + cleans up on workspace switch / logout / unmount
 *   - Exposes notifications, unreadCount, isConnected via useNotifications()
 *   - Exposes mark-read actions that PATCH Central + update local state
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  NOTIFICATION_INBOX_PATH,
  NOTIFICATION_UNREAD_COUNT_PATH,
  NOTIFICATION_MARK_READ_PATH,
  type CentralNotification,
  type InboxResponse,
  type UnreadCountResponse,
  type UnreadCountEvent,
  type NotificationSSEClient,
} from '@levelup/central-notifications';
import {
  type CentralEventStreamClient,
  type CentralBusinessEventType,
  type CentralEventPayload,
} from '@levelup/central-events';

import { selectCentralAccessToken } from '../store/slices/centralAuthSlice';
import { selectBootCurrentWorkspace } from '../store/slices/bootContextSlice';
import { CENTRAL_URL, APP_ID } from '../config/centralAuth';
import { createNotificationStreamClient } from '../services/notificationStream';
import { createEventStreamClient } from '../services/centralEventStream';
import {
  invalidateAfterCentralReconnect,
  invalidateForCentralEvent,
} from '../store/api/centralEventInvalidation';

// ──────────────────────────────────────────────────────────────
// Public types
// ──────────────────────────────────────────────────────────────

export type NotificationTab = 'app' | 'workspace';

export interface NotificationListItem {
  id: string;
  title: string;
  body: string | null;
  isRead: boolean;
  createdAt: string;
  appId: string | null;
  type: string;
  /** Derived: tab classification for the panel */
  tab: NotificationTab;
  /** Original Central notification payload (for actions / metadata) */
  raw: CentralNotification;
}

export interface NotificationContextValue {
  notifications: NotificationListItem[];
  /** Total unread (across both tabs) */
  unreadCount: number;
  /** Whether the notification SSE stream is connected */
  isConnected: boolean;
  /** Whether the initial inbox pre-fetch is in flight */
  isInitialLoading: boolean;
  /** Last error from REST fetch or PATCH (cleared on next success) */
  lastError: string | null;
  /** Mark a single notification as read (POST mark-read + local update) */
  markAsRead: (notificationId: string) => Promise<void>;
  /** Mark all currently-loaded notifications as read */
  markAllAsRead: () => Promise<void>;
  /** Force-refresh inbox from Central */
  refresh: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

// ──────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────

const MAX_NOTIFICATIONS = 50;

function classifyNotification(n: CentralNotification): NotificationTab {
  // Notifications scoped to this app belong in "This App" tab.
  // Workspace-scoped notifications (no appId) live in "Workspace" tab.
  if (!n.appId) return 'workspace';
  return n.appId === APP_ID ? 'app' : 'workspace';
}

function toListItem(n: CentralNotification): NotificationListItem {
  return {
    id: n.id,
    title: n.title,
    body: n.body,
    isRead: n.isRead,
    createdAt: n.createdAt,
    appId: n.appId,
    type: n.type,
    tab: classifyNotification(n),
    raw: n,
  };
}

function authHeaders(token: string, extra: Record<string, string> = {}): Record<string, string> {
  return {
    Authorization: `Bearer ${token}`,
    Accept: 'application/json',
    ...extra,
  };
}

// ──────────────────────────────────────────────────────────────
// Provider
// ──────────────────────────────────────────────────────────────

export function NotificationProvider({ children }: { children: ReactNode }) {
  const dispatch = useDispatch();
  const token = useSelector(selectCentralAccessToken);
  const workspace = useSelector(selectBootCurrentWorkspace);
  const workspaceId = workspace?.id ?? null;

  const [notifications, setNotifications] = useState<NotificationListItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);

  // SSE client refs — singletons for the lifetime of the (token, workspaceId) tuple
  const notifClientRef = useRef<NotificationSSEClient | null>(null);
  const eventClientRef = useRef<CentralEventStreamClient | null>(null);

  // ── Mark-read API ─────────────────────────────────────
  const markAsRead = useCallback(
    async (notificationId: string) => {
      // Optimistic
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n)),
      );
      setUnreadCount((c) => Math.max(0, c - 1));

      if (!token) return;
      try {
        const res = await fetch(`${CENTRAL_URL}${NOTIFICATION_MARK_READ_PATH}`, {
          method: 'POST',
          headers: authHeaders(token, { 'Content-Type': 'application/json' }),
          body: JSON.stringify({ notificationIds: [notificationId] }),
        });
        if (!res.ok) throw new Error(`Mark-read failed: ${res.status}`);
      } catch (err) {
        // Revert optimistic update
        setNotifications((prev) =>
          prev.map((n) => (n.id === notificationId ? { ...n, isRead: false } : n)),
        );
        setUnreadCount((c) => c + 1);
        setLastError(err instanceof Error ? err.message : 'Mark-read failed');
      }
    },
    [token],
  );

  const markAllAsRead = useCallback(async () => {
    const previouslyUnread = notifications.filter((n) => !n.isRead);
    if (previouslyUnread.length === 0) return;

    // Optimistic
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    setUnreadCount(0);

    if (!token || !workspaceId) return;
    try {
      const res = await fetch(`${CENTRAL_URL}${NOTIFICATION_MARK_READ_PATH}`, {
        method: 'POST',
        headers: authHeaders(token, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({ workspaceId, appId: APP_ID }),
      });
      if (!res.ok) throw new Error(`Mark-all-read failed: ${res.status}`);
    } catch (err) {
      // Revert
      setNotifications((prev) =>
        prev.map((n) => {
          const wasUnread = previouslyUnread.some((u) => u.id === n.id);
          return wasUnread ? { ...n, isRead: false } : n;
        }),
      );
      setUnreadCount(previouslyUnread.length);
      setLastError(err instanceof Error ? err.message : 'Mark-all-read failed');
    }
  }, [notifications, token, workspaceId]);

  // ── REST fetch (initial inbox + unread count) ────────
  const fetchInitial = useCallback(async () => {
    if (!token || !workspaceId) return;
    setIsInitialLoading(true);
    setLastError(null);

    try {
      const [inboxRes, countRes] = await Promise.all([
        fetch(
          `${CENTRAL_URL}${NOTIFICATION_INBOX_PATH}?workspaceId=${encodeURIComponent(workspaceId)}&limit=${MAX_NOTIFICATIONS}`,
          { headers: authHeaders(token) },
        ),
        fetch(
          `${CENTRAL_URL}${NOTIFICATION_UNREAD_COUNT_PATH}?workspaceId=${encodeURIComponent(workspaceId)}`,
          { headers: authHeaders(token) },
        ),
      ]);

      if (inboxRes.ok) {
        const body = (await inboxRes.json()) as InboxResponse;
        setNotifications((body.notifications ?? []).map(toListItem));
      }
      if (countRes.ok) {
        const body = (await countRes.json()) as UnreadCountResponse;
        setUnreadCount(body.unreadCount ?? 0);
      }
    } catch (err) {
      setLastError(err instanceof Error ? err.message : 'Failed to load inbox');
    } finally {
      setIsInitialLoading(false);
    }
  }, [token, workspaceId]);

  // ── SSE lifecycle (singleton per token/workspace) ────
  useEffect(() => {
    if (!token || !workspaceId) return;

    // 1) Initial REST fetch
    fetchInitial();

    // 2) Notification SSE client (CentralNotification + UnreadCountEvent)
    const notifClient = createNotificationStreamClient();
    notifClientRef.current = notifClient;

    notifClient.onNotification((n: CentralNotification) => {
      const item = toListItem(n);
      setNotifications((prev) => {
        // Dedupe by id, prepend new, cap to MAX
        const without = prev.filter((p) => p.id !== item.id);
        return [item, ...without].slice(0, MAX_NOTIFICATIONS);
      });
      if (!n.isRead) setUnreadCount((c) => c + 1);
    });

    notifClient.onUnreadCount((evt: UnreadCountEvent) => {
      setUnreadCount(evt.count);
    });

    notifClient.onConnectionChange((connected: boolean) => {
      setIsConnected(connected);
    });

    notifClient.onError((message: string) => {
      setLastError(message);
    });

    // .connect() returns a promise but errors flow through onError
    void notifClient.connect(workspaceId, token);

    // 3) Central event stream — used to refresh boot context on
    //    entitlement/credit/subscription/feature-flag changes. We do NOT
    //    synthesize fake notifications here; the real notification feed
    //    is the NotificationSSEClient above.
    const eventClient = createEventStreamClient(token, workspaceId, {
      onEvent: (eventType: CentralBusinessEventType, payload: CentralEventPayload) => {
        // Access changes Central pushes invalidate the BeHeard reads derived
        // from them (capabilities, onboarding/me, gamification, public
        // profile, challenges, assignments) — on the event, never by polling.
        // See store/api/centralEventInvalidation.ts for the mapping.
        const tags = invalidateForCentralEvent(dispatch, eventType, payload, workspaceId);
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.debug('[CentralEvents]', eventType, tags);
        }
      },
      // Events sent while disconnected were missed: catch up once.
      onReconnect: () => invalidateAfterCentralReconnect(dispatch),
    });
    eventClientRef.current = eventClient;
    eventClient.connect();

    return () => {
      notifClient.disconnect();
      eventClient.disconnect();
      notifClientRef.current = null;
      eventClientRef.current = null;
      setIsConnected(false);
    };
  }, [token, workspaceId, fetchInitial, dispatch]);

  const value = useMemo<NotificationContextValue>(
    () => ({
      notifications,
      unreadCount,
      isConnected,
      isInitialLoading,
      lastError,
      markAsRead,
      markAllAsRead,
      refresh: fetchInitial,
    }),
    [notifications, unreadCount, isConnected, isInitialLoading, lastError, markAsRead, markAllAsRead, fetchInitial],
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

// ──────────────────────────────────────────────────────────────
// Hook
// ──────────────────────────────────────────────────────────────

/**
 * Read the global notification state. Must be inside <NotificationProvider>.
 *
 * Returns a safe default when used outside the provider so the UI doesn't
 * crash during boot or in storybook contexts.
 */
export function useNotifications(): NotificationContextValue {
  const ctx = useContext(NotificationContext);
  if (ctx) return ctx;
  // Safe default (provider not mounted yet — e.g. before BootGate completes)
  return {
    notifications: [],
    unreadCount: 0,
    isConnected: false,
    isInitialLoading: false,
    lastError: null,
    markAsRead: async () => undefined,
    markAllAsRead: async () => undefined,
    refresh: async () => undefined,
  };
}
